import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
} from '@mui/material';
import {
  Chat as LineIcon,
  Link as LinkIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import api from '../../services/api';
import {
  StatCard,
  SectionLoading,
  SectionError,
  COLORS,
  formatNumber,
  formatPct,
  formatMonth,
} from './shared';

interface LineData {
  total_customers: number;
  linked_customers: number;
  link_rate: number;
  new_30d: number;
  new_linked_30d: number;
  new_link_rate_30d: number;
  cohort: {
    month: string;
    customers: number;
    linked: number;
    link_rate: number;
  }[];
  retention_comparison: {
    linked: { eligible: number; retained: number; retention_pct: number };
    email_only: { eligible: number; retained: number; retention_pct: number };
  };
}

const LineLinkSection: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LineData | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get('/api/retention/line-rate')
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} />;
  if (!data) return <SectionError />;

  const linkedRetention = data.retention_comparison.linked.retention_pct;
  const emailOnlyRetention = data.retention_comparison.email_only.retention_pct;
  const lift =
    emailOnlyRetention > 0
      ? +((linkedRetention - emailOnlyRetention) / emailOnlyRetention * 100).toFixed(1)
      : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {data.linked_customers === 0 && (
        <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
          目前沒有客戶綁定 LINE。需建置「綁定 LINE 領 NT$150 優惠」的 onboarding 流程。
        </Alert>
      )}

      {/* Headline */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="LINE 綁定率"
            value={formatPct(data.link_rate)}
            subtitle={`${formatNumber(data.linked_customers)} / ${formatNumber(data.total_customers)} 位客戶`}
            icon={<LinkIcon />}
            color={COLORS.line}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="30 天新綁定率"
            value={formatPct(data.new_link_rate_30d)}
            subtitle={`${formatNumber(data.new_linked_30d)} / ${formatNumber(data.new_30d)} 位新客`}
            icon={<TrendingIcon />}
            color={COLORS.primary}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="綁定客戶留存"
            value={formatPct(linkedRetention)}
            subtitle={`${data.retention_comparison.linked.retained} / ${data.retention_comparison.linked.eligible}`}
            icon={<LineIcon />}
            color={COLORS.success}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="未綁定客戶留存"
            value={formatPct(emailOnlyRetention)}
            subtitle={
              lift !== 0
                ? `綁定 vs 未綁定差距 ${lift > 0 ? '+' : ''}${lift}%`
                : `${data.retention_comparison.email_only.retained} / ${data.retention_comparison.email_only.eligible}`
            }
            icon={<LineIcon />}
            color={COLORS.slate}
          />
        </Grid>
      </Grid>

      {/* Cohort link rate over time */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="註冊月份 LINE 綁定率"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
          subheader="每月新註冊客戶的 LINE 綁定比例"
          subheaderTypographyProps={{ fontSize: '0.75rem' }}
        />
        <CardContent sx={{ pt: 0 }}>
          {data.cohort.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              資料不足
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart
                data={data.cohort.map((c) => ({ ...c, monthLabel: formatMonth(c.month) }))}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="monthLabel" fontSize={11} />
                <YAxis yAxisId="left" fontSize={11} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  fontSize={11}
                  tickFormatter={(v) => `${v}%`}
                  domain={[0, 100]}
                />
                <Tooltip
                  formatter={(v: any, name: string) =>
                    name === '綁定率' ? `${v}%` : formatNumber(v)
                  }
                />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Bar
                  yAxisId="left"
                  dataKey="customers"
                  name="新註冊"
                  fill={COLORS.primary}
                  opacity={0.25}
                />
                <Bar
                  yAxisId="left"
                  dataKey="linked"
                  name="其中綁定 LINE"
                  fill={COLORS.line}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="link_rate"
                  name="綁定率"
                  stroke={COLORS.warning}
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Insight box */}
      <Card
        variant="outlined"
        sx={{
          border: `1px solid ${COLORS.line}`,
          bgcolor: `${COLORS.line}08`,
        }}
      >
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            下一步建議
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            {data.linked_customers === 0 ? (
              <>
                目前 LINE 綁定量為 0。建議在 checkout 成功頁 / 會員中心加入「綁定 LINE 領 NT$150 下次折扣」按鈕，
                並在 Day 5 再行銷訊息中附綁定連結。預期 30 天內將 20-30% 新客綁定，為 Q2 召回自動化打好基礎。
              </>
            ) : lift > 0 ? (
              <>
                綁定客戶留存率高出 {lift}%。建議擴大綁定誘因，
                目標 90 天內將綁定率從 {formatPct(data.link_rate)} 提升到 40%+。
              </>
            ) : (
              <>
                資料量尚不足以比較留存差異（綁定客戶樣本過少）。
                建議先累積至少 200 位綁定客戶後再重新檢視。
              </>
            )}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LineLinkSection;
